/* eslint-disable react/no-unescaped-entities */
import { NextPage } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { Button } from '@jove-frontend/uikit';
import { lintrk } from 'nextjs-linkedin-insight-tag';
import { useAnalytics } from 'use-analytics';
import AXALogo from '../../assets/axa-logo.png';
import HeroImage from '../../assets/business-insurance-hero.jpg';
import USP3 from '../../assets/claims.png';
import CubeImg from '../../assets/cube_blue.png';
import USP2 from '../../assets/dynamic.png';
import USP1 from '../../assets/instant-quotes.png';
import MarkelLogo from '../../assets/markel-logo.png';
import PyramidImg from '../../assets/pyramid_green.png';
import USP4 from '../../assets/user_jasmine.jpg';
import USP6 from '../../assets/user_sarah.jpg';
import USP5 from '../../assets/user_studio.jpg';
import WakamLogo from '../../assets/wakam-logo.png';
import S1 from '../../assets/zero_percentage.png';
import { FeatureBlock, SectionHeading } from '../../components/Content';
import FeatureSection from '../../components/FeatureSection';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import { LearnMoreIcon } from '../../components/Logos';
import { useHubspotModal } from '../../components/Modals/HubspotModal';
import WidgetEmbed from '../../components/WidgetEmbed';
import { handleBookDemo } from '../../util';
import {
  features,
  instantPricingList,
} from '../personal/old-business-insurance';

const LinkedinConversionId = process.env.NX_LINKEDIN_CONVERSION_ID;

const WamoBankPage: NextPage = () => {
  const { track } = useAnalytics();
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState<boolean>(false);

  const { HubspotModal, setShowHubspotModal } = useHubspotModal({
    props: {
      formId: 'da63a1b7-3916-4288-a244-120de7cb8d4b',
      title: 'Switch to Jove at renewal',
      description: (
        <>
          Send us your current insurance policy and let us try to beat your
          quote at renewal, subject to{' '}
          <Link
            href="/legal/terms-and-conditions"
            target="_blank"
            className="text-brand-primary"
          >
            Jove's T&C.
          </Link>
        </>
      ),
    },
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('message', function (e) {
      // if (e.origin !== 'localhost') return;
      // Close the iframe request coming from the widget

      if (e.data === 'close-iframe') {
        setIsQuoteModalOpen(false);
      }
    });
  }

  // each time modal is opened, track it
  useEffect(() => {
    if (isQuoteModalOpen) {
      track('open_quote_modal');
      track('click_get_quote_button');
      lintrk('track', LinkedinConversionId);
    }
  }, [isQuoteModalOpen]);

  return (
    <div className="mx-auto w-full">
      <Header />
      {/* hero section */}
      <div className="relative z-0 w-full bg-[url('../assets/business-hero-bg.jpg')] bg-[length:1440px_380px] bg-center pb-14 pt-5">
        <div className="container mx-auto">
          <div className="relative grid grid-cols-1 items-center gap-4 px-6 md:grid-cols-2">
            <div className="align-center flex w-full flex-col md:max-w-md">
              <h1 className="mb-5 text-center text-4xl font-black text-white md:text-left md:text-6xl">
                Insurance that works as hard as you do
              </h1>

              <p className="text-center text-2xl text-white md:text-left">
                Fast. Fair. Flexible.
              </p>

              <div className="mt-14 flex justify-center space-x-4 lg:block">
                <Button
                  onClick={() => setIsQuoteModalOpen(true)}
                  className="px-16"
                >
                  Get a quote
                </Button>
              </div>
            </div>

            <div className="hidden md:block">
              <Image
                priority
                placeholder="blur"
                src={HeroImage}
                layout="responsive"
                width="586"
                height="586"
                alt="Insurance that works as hard as you do"
              />
            </div>
          </div>
        </div>
      </div>

      {/* switch banner */}
      <div className="relative min-h-[329px] bg-[url('../assets/bg-pattern.jpg')] bg-[length:1440px_329px] bg-center bg-repeat-x">
        <div className="relative mx-auto grid h-full min-h-[329px] grid-cols-1 lg:container md:grid-cols-3">
          <div className="left-0 z-20 -mt-10 hidden flex-shrink-0 md:absolute md:block">
            <Image
              src={S1}
              alt="Switch to Jove at renewal"
              priority={true}
              layout="intrinsic"
              placeholder="blur"
              width={345}
              height={355}
            />
          </div>

          <div className="col-span-2 flex flex-col items-start justify-center space-y-4 px-6 text-white sm:col-start-2">
            <h2 className="text-center text-4xl font-extrabold text-white md:text-left">
              Switch to Jove at renewal
            </h2>
            <p className="text-center text-lg md:text-left">
              Interested in quoting or switching to Jove, but need to wait until
              your existing insurance expires? Send us your insurance renewal
              and let us try to beat your quote by <strong>5%</strong>.
            </p>
            <Button
              color="secondary-outline"
              className="mx-auto bg-white md:mx-0"
              onClick={() => setShowHubspotModal(true)}
            >
              Switch at renewal
            </Button>
          </div>
        </div>
      </div>

      {/* content section */}
      <div className="bg-gray-50 px-4 py-12 md:py-24">
        <SectionHeading
          title="We've got you covered"
          description="We offer specialist business insurance to contractors, consultants,
            professionals, advisors and SMEs."
        />

        <div className="container mx-auto space-y-10">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-16">
            {features.slice(0, 3).map((feature, index) => (
              <FeatureBlock key={index} {...feature} />
            ))}
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-16 lg:grid-cols-4">
            {features.slice(3).map((feature, index) => (
              <FeatureBlock key={index} {...feature} />
            ))}
          </div>

          <div className="mt-12 flex justify-center space-x-4">
            <Button onClick={() => setIsQuoteModalOpen(true)}>
              Get a quote
            </Button>
          </div>
        </div>
      </div>
      {/* who is it for section */}
      <div className="container mx-auto px-4 py-12 md:py-24">
        <SectionHeading
          title="Who is business insurance for?"
          description="If your business deals with clients, contractors, suppliers or members
          of the public, business insurance is designed to protect you against
          compensations claims for Injury, property damage, financial loss also
          provide legal assistance to defend claims."
        />

        <div className="my-10 space-y-10">
          <FeatureSection index={1} image={USP4}>
            <div className="text-lg">
              <p className="text-brand-secondary text-[32px] font-extrabold leading-9">
                Jasmine Reema
              </p>
              <p>Limited company director</p>

              <p className="my-5 font-bold">
                Professional indemnity : £1,000,000
              </p>

              <hr className="my-5" />

              <div className="space-y-4">
                <p>
                  <strong className="block">The scenario</strong>A project is
                  significantly delayed due to mis-communication between the
                  project manager and the senior executives of the company.
                </p>

                <p>
                  <strong className="block">The outcome</strong>"Our policy
                  covered the costs of rectifying the issue and any legal costs
                  involved."
                </p>
                <Button onClick={() => setIsQuoteModalOpen(true)}>
                  Get a quote
                </Button>
              </div>
            </div>
          </FeatureSection>

          <footer className="text-center">
            <p className="opacity-70">
              Some professions are likely to require professional indemnity
              insurance due to their respective governing bodies:
            </p>
            <p>
              Accountants / Financial Consultants / Surveyors / Engineers and
              Healthcare professionals.
            </p>
          </footer>
        </div>

        <div className="my-10 space-y-10">
          <FeatureSection index={2} image={USP5}>
            <div className="text-lg">
              <p className="text-brand-secondary text-[32px] font-extrabold leading-9">
                Creative XYZ Limited
              </p>
              <p>Small medium enterprise</p>

              <p className="my-5 font-bold">
                Employers liability : £10,000,000
              </p>

              <hr className="my-5" />

              <div className="space-y-4">
                <p>
                  <strong className="block">The scenario</strong>"An employee
                  went on to a clients site without correct PPE. They were
                  subsequently injured whilst working".
                </p>

                <p>
                  <strong className="block">The outcome</strong>"The employee
                  injury and rehabilitation claims was dealt with by the insurer
                  and our process changed to prevent this happening again"
                </p>
                <Button onClick={() => setIsQuoteModalOpen(true)}>
                  Get a quote
                </Button>
              </div>
            </div>
          </FeatureSection>

          <footer className="text-center">
            <p className="opacity-70">
              Employers Liability is a legal requirement for most businesses
              that employ at least 1 member of staff.
            </p>
            <p>
              Consultancy house / Software development house / Creative agencise
              / Startups / Tech companies / Legal firm
            </p>
          </footer>
        </div>

        <div className="my-10 space-y-10">
          <FeatureSection index={3} image={USP6}>
            <div className="text-lg">
              <p className="text-brand-secondary text-[32px] font-extrabold leading-9">
                Sarah Watkins
              </p>
              <p>Sole trader, Freelancer</p>

              <p className="my-5 font-bold">Public liability : £1,000,000</p>

              <hr className="my-5" />

              <div className="space-y-4">
                <p>
                  <strong className="block">The scenario</strong>"I had a
                  meeting with my client in our office space. The client tripped
                  over and injured his wrist."
                </p>

                <p>
                  <strong className="block">The outcome</strong>"The injury and
                  rehabilitation claims was dealt with by the insurer and our
                  process changed to prevent this happening again"
                </p>
                <Button onClick={() => setIsQuoteModalOpen(true)}>
                  Get a quote
                </Button>
              </div>
            </div>
          </FeatureSection>
          <footer className="text-center">
            <p className="opacity-70">
              Other professsions that often need public liability insurance:
            </p>
            <p>
              Management consultant / IT contractor / Engineering contractor /
              Fitness professionals / Photographers / Teacher/tutor
            </p>
          </footer>
        </div>
      </div>
      {/* instant pricing section */}
      <div className="bg-gray-50 px-4 py-12 md:py-24">
        <div className="container mx-auto grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-44">
          <div className="flex flex-col justify-evenly space-y-10">
            <h3 className="text-brand-secondary text-center text-4xl font-extrabold md:text-5xl lg:text-left">
              Get instant pricing
            </h3>

            <ul className="mt-5 space-y-10">
              {instantPricingList.map((item, index) => (
                <li key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <div className="space-y-4">
                    <p className="text-2xl font-black">{item.title}</p>
                    <p className="text-base">{item.description}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mx-auto min-h-[470px] w-full sm:w-[440px]">
            <div className="absolute -left-10 top-2/4 z-40 hidden sm:-left-20 sm:top-[60%] sm:block ">
              <Image src={PyramidImg} alt="Pyramid" width={158} height={158} />
            </div>
            <div className="absolute right-10 top-[20%] z-20 hidden sm:-right-10 sm:block ">
              <Image src={CubeImg} alt="Pyramid" width={240} height={240} />
            </div>
            <div className="relative left-0 top-0 z-30 mx-auto w-[350px] rounded-3xl bg-white sm:absolute sm:min-h-[470px] sm:min-w-[370px]">
              <header className="bg-gray-dark rounded-t-3xl py-6">
                <p className="text-center text-2xl font-bold text-white">
                  Jove business insurance
                </p>
              </header>

              <div
                className="flex flex-col items-center space-y-5 rounded-b-3xl p-6"
                style={{
                  boxShadow: ' inset 9px -8px 30px -5px rgba(0,0,0,0.49)',
                }}
              >
                <span className="text-xl leading-none">From</span>
                <span
                  className="text-brand-secondary text-8xl font-medium leading-none"
                  style={{
                    fontFeatureSettings: "'pnum' on, 'lnum' on, 'liga' off",
                  }}
                >
                  £0.15
                </span>
                <span className="text-2xl leading-none">Per day *</span>

                <span className="border-gray-dark h-6 w-16 border-b-4"></span>
                <span className="text-center text-lg">
                  * Less than a bottle of
                  <br /> water from Tesco
                </span>

                <Button
                  className="w-full"
                  onClick={() => setIsQuoteModalOpen(true)}
                >
                  Get a quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* why jove section */}
      <div className="px-4 py-12 md:py-24">
        <div className="container mx-auto space-y-20 text-center">
          <SectionHeading
            title="Why use Jove"
            description="We are able to cover you against most situations."
          />

          <div className="container mx-auto flex flex-col items-center justify-center space-y-10 md:flex-row md:items-start md:space-x-10 md:space-y-0 ">
            <div className="flex max-w-xs flex-col items-center space-y-4">
              <div className="bg-teal max-w-xs/2 flex items-center justify-start rounded-full p-10">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M32.5 25V7.5L10 35H27.5L27.5 52.5L50 25L32.5 25Z"
                    stroke="#111827"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <p className="text-base">
                Speedy digital claims process, no more being on hold while you
                wait for an operator
              </p>
            </div>

            <div className="flex max-w-xs flex-col items-center space-y-4">
              <div className="bg-teal max-w-xs/2 flex items-center justify-start rounded-full p-10">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M27.2629 46.4655V6.875L18.4914 11.8534L7.34912 13.2759V18.0172L8.77153 30.819L14.6983 39.5905L27.2629 46.4655Z"
                    fill="#75BCB4"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M25.9714 5.04011C26.8415 4.26136 28.158 4.26137 29.0281 5.04013C33.4895 9.03323 39.3754 11.4586 45.8333 11.4586C46.2694 11.4586 46.7028 11.4475 47.1332 11.4257C48.2197 11.3706 49.1952 12.0869 49.4678 13.1402C50.0875 15.5343 50.4166 18.0431 50.4166 20.6253C50.4166 34.5102 40.92 46.1712 28.0711 49.4783C27.6964 49.5747 27.3034 49.5747 26.9287 49.4783C14.0799 46.1712 4.58325 34.5102 4.58325 20.6253C4.58325 18.043 4.91235 15.5341 5.53208 13.1399C5.80469 12.0867 6.78021 11.3703 7.86671 11.4254C8.29705 11.4472 8.73042 11.4583 9.16659 11.4583C15.6243 11.4583 21.5101 9.03305 25.9714 5.04011ZM9.58242 16.0388C9.30945 17.5247 9.16659 19.0574 9.16659 20.6253C9.16659 32.1616 16.918 41.8931 27.4999 44.8853C38.0818 41.8931 45.8333 32.1616 45.8333 20.6253C45.8333 19.0575 45.6904 17.5249 45.4175 16.039C38.6656 15.9466 32.4528 13.6059 27.4997 9.7342C22.5468 13.6057 16.3341 15.9463 9.58242 16.0388ZM35.9954 21.2965C36.8903 22.1915 36.8903 23.6425 35.9954 24.5374L26.8287 33.7041C25.9338 34.599 24.4827 34.599 23.5878 33.7041L19.0045 29.1208C18.1095 28.2258 18.1095 26.7748 19.0045 25.8799C19.8994 24.9849 21.3504 24.9849 22.2454 25.8799L25.2083 28.8427L32.7545 21.2965C33.6494 20.4016 35.1004 20.4016 35.9954 21.2965Z"
                    fill="#333333"
                  />
                </svg>
              </div>

              <p className="text-base">
                Transparent on what you are covered for and what you are not.
                Don&apos;t need to be a lawyer to understand your policy
              </p>
            </div>

            <div className="flex max-w-xs flex-col items-center space-y-4">
              <div className="bg-teal max-w-xs/2 flex items-center justify-start rounded-full p-10">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M50.5 30C50.5 41.3218 41.3218 50.5 30 50.5V54.5C43.531 54.5 54.5 43.531 54.5 30H50.5ZM30 50.5C18.6782 50.5 9.5 41.3218 9.5 30H5.5C5.5 43.531 16.469 54.5 30 54.5V50.5ZM9.5 30C9.5 18.6782 18.6782 9.5 30 9.5V5.5C16.469 5.5 5.5 16.469 5.5 30H9.5ZM30 9.5C41.3218 9.5 50.5 18.6782 50.5 30H54.5C54.5 16.469 43.531 5.5 30 5.5V9.5ZM38 30C38 34.4183 34.4183 38 30 38V42C36.6274 42 42 36.6274 42 30H38ZM30 38C25.5817 38 22 34.4183 22 30H18C18 36.6274 23.3726 42 30 42V38ZM22 30C22 25.5817 25.5817 22 30 22V18C23.3726 18 18 23.3726 18 30H22ZM30 22C34.4183 22 38 25.5817 38 30H42C42 23.3726 36.6274 18 30 18V22ZM44.4957 12.6759L35.6569 21.5147L38.4853 24.3431L47.3241 15.5043L44.4957 12.6759ZM35.6569 38.4853L44.4957 47.3241L47.3241 44.4957L38.4853 35.6569L35.6569 38.4853ZM24.3431 21.5147L15.5043 12.6759L12.6759 15.5043L21.5147 24.3431L24.3431 21.5147ZM21.5147 35.6569L12.6759 44.4957L15.5043 47.3241L24.3431 38.4853L21.5147 35.6569Z"
                    fill="#111827"
                  />
                </svg>
              </div>

              <p className="text-base">
                Super easy management of policies, add, remove, increase or
                change is all online
              </p>
            </div>
          </div>
          <Button
            onClick={() => setIsQuoteModalOpen(true)}
            className="mx-auto md:mx-0"
          >
            Get a quote
          </Button>
        </div>
      </div>
      {/* how it works section */}
      <div className="bg-gray-50 px-4 py-12 md:py-24">
        <div className="container space-y-10">
          <h3 className="text-brand-secondary text-center text-4xl font-extrabold md:text-5xl">
            How it works
          </h3>

          <FeatureSection index={1} image={USP1}>
            <h4 className="mb-5 text-xl font-extrabold leading-snug text-black md:text-3xl">
              1. Simply click and go
            </h4>
            <p className="text-gray-dark text-md md:text-lg lg:max-w-lg">
              Tell us about yourself and what you’d like covered and we’ll show
              you your quote while your choosing your terms.
            </p>
          </FeatureSection>

          <FeatureSection index={2} image={USP2}>
            <h4 className="mb-5 text-xl font-extrabold leading-snug text-black md:text-3xl">
              2. Transparent policies
            </h4>
            <p className="text-gray-dark text-md md:text-lg lg:max-w-lg">
              See, manage, and update your cover through the Jove dashboard. See
              how changes like pausing your policy or changing your location
              affect your payments.
            </p>
          </FeatureSection>

          <FeatureSection index={3} image={USP3}>
            <h4 className="mb-5 text-xl font-extrabold leading-snug text-black md:text-3xl">
              3. Simple claims processes
            </h4>
            <p className="text-gray-dark text-md md:text-lg lg:max-w-lg">
              We think insurance is only as good as its claims process. That’s
              why ours is second to none.
            </p>
          </FeatureSection>
        </div>
      </div>
      {/* partners section */}
      <div className="bg-gray-whitw px-4 py-12 md:py-24">
        <div className="container mx-auto text-center">
          <SectionHeading
            title="Our partners"
            description="Jove's product is backed by top rated insurers"
          />

          <div className="mx-auto mt-20 flex items-center justify-evenly space-x-4 lg:space-x-0">
            <Image src={AXALogo} alt="AXA" height={104} width={104} />
            <Image src={WakamLogo} alt="Wakam" height={46} width={218} />
            <Image src={MarkelLogo} alt="Markel" height={121} width={101} />
          </div>
        </div>
      </div>
      {/* bespoke insurance section */}
      <div className="bg-gray-50 px-4 py-12 md:py-24">
        <div className="container mx-auto space-y-10 text-center">
          <div>
            <LearnMoreIcon className="mx-auto" />

            <SectionHeading title="Bespoke insurance requirements?" />
          </div>
          <p className="mx-auto max-w-4xl text-xl">
            Arrange a call with one of our insurance experts if you have bespoke
            requirements. We will curate the market to deliver the best solution
            that works for you.
          </p>

          <Button onClick={() => handleBookDemo()} className="mx-auto md:mx-0">
            Arrange a call
          </Button>
        </div>
      </div>
      <Footer />

      <HubspotModal />

      <WidgetEmbed isOpen={isQuoteModalOpen} theme="wamo" referrer="wamo" />
    </div>
  );
};

export default WamoBankPage;
